import type { TFunction } from 'i18next';
import { values } from 'ramda';
import type {
  CMSCountryParams,
  CMSGroupParams,
  CMSScheduler,
} from '@/api/cms/base';
import type { GameCategory, GameV1, GameV2, Real } from '@/api/games';
import { createItems } from './common';
import type { ProviderCustom } from './gameCategories';

export enum GameStaticCategories {
  Favorites = 'favorites',
  Lobby = 'lobby',
  Recent = 'recent',
  HOT_AND_COLD = 'hot_and_cold',
}

const categoriesWithTrans = [
  'lobby',
  'all',
  'trending',
  'slots',
  'new',
  'instant_wins',
  'bonus_buy',
  'btc',
  'tables',
  'live',
  'jackpot',
  'exclusive',
  'halloween',
  'mascot_lottery',
  'recent',
  'casual',
  'top',
  'legends_en',
  'legends',
  'hits',
  'top_vip',
];

const getGameCategoriesTransMapP =
  (keys: string[]) => (category: string, t: TFunction) =>
    keys.includes(category) ? t(`common:gameCategories.${category}`) : category;

export const getGameCategoriesTransMap =
  getGameCategoriesTransMapP(categoriesWithTrans);

export interface UpdatedGameCategory
  extends GameCategory,
    CMSScheduler,
    CMSGroupParams,
    CMSCountryParams {
  categoryIdAuth: string;
  categoryIdNotAuth: string;
  defaultCategoryId: string;
  order: number;
  href: string;
  showOnLobby?: boolean;
  filterTitle?: string;
}

const topicsWithTrans = [
  'megaways',
  'hold_win_respin',
  'classics',
  'fruit_sweet_gem',
  'ancient_treasure',
  'wild',
  'wild_west',
  'fast_wins',
  'big_wins',
  'magic',
  'book',
  'space',
  'ocean_treasures',
  'asian_wonders',
  'coins',
  'gods',
];

const getGameTopicsTransMapP =
  (keys: string[]) => (topic: string, t: TFunction) => {
    const topicName = topic.replace('@', '');

    return keys.includes(topicName)
      ? t(`common:gameTopics.${topicName}`)
      : topicName;
  };

export const getGameTopicsTransMap = getGameTopicsTransMapP(topicsWithTrans);

export const providersInvertLogo = [
  'bulletproof',
  'jaderabbit',
  'mascot',
  'petersons',
  'baddingo',
  'spribe',
  'truelab',
  'stormcraft',
  'sthlmgaming',
  'spearhead',
  'relax',
  'reevo',
  'realdealer',
  'maxwingaming',
  'givme',
  'goldenrock',
  'gamevy',
  'gameswarehouse',
  'fantasma',
  'dtech',
  'dreamtech',
  'avatarux',
  'arcadem',
  '100hp',
  'bsg',
  'bitpunch',
  'blackcat',
  'evolution',
  'ezugi',
  'flatdog',
  'foxhoundgames',
  'gameart',
  'gamzix',
  'hacksaw',
  'inout',
  'jinxgaming',
  'kitsunegaming',
  'koala',
  'lakka',
  'madhatgames',
  'pgsoft',
  'pineappleplay',
  'tomhornnative',
  'trustygaming',
  'winfinity',
];

interface SortedProvidersI {
  [char: string]: ProviderCustom[];
}

export function sortProvidersByAlphabet(providers: ProviderCustom[]) {
  return providers.reduce((obj, provider) => {
    const firstChar = provider.title[0].toLowerCase();
    const charGroupKey = Number(firstChar) ? '0-9' : firstChar;
    const charGroup = obj[charGroupKey];

    if (charGroup) {
      return {
        ...obj,
        [charGroupKey]: [...charGroup, provider],
      };
    }
    return {
      ...obj,
      [charGroupKey]: [provider],
    };
  }, {} as SortedProvidersI);
}

export type GameQueryParameters = {
  demo?: string;
};

export const getIsTop = (categoryId: string) => /^top/.test(categoryId);

export const isGamePage = (pathname: string) => pathname.includes('/game/');

export const isFavoriteGame = (currencies: Real, favoriteGames: GameV1[]) =>
  values(currencies)
    .map(({ id }) => id)
    .some((currencyId) =>
      favoriteGames.some(({ real }) =>
        values(real).some(({ id }) => id === currencyId)
      )
    );

export const getGamesPlugs = (
  gamesList: GameV1[],
  gamesPerRow: number,
  slidesPerView: number,
  options?: {
    onlyFirstRow?: boolean;
  }
) => {
  if (
    gamesList.length === 0 ||
    (options?.onlyFirstRow && gamesList.length >= slidesPerView)
  ) {
    return [];
  }

  const gridArea = gamesPerRow * Math.ceil(slidesPerView);

  let numberOfGamesPlug =
    gamesList.length <= gridArea
      ? gridArea - gamesList.length
      : gamesList.length % gridArea;

  if (gamesList.length > gridArea) {
    if (numberOfGamesPlug === gamesList.length) {
      return [];
    }
    if (numberOfGamesPlug >= gamesPerRow) {
      numberOfGamesPlug = numberOfGamesPlug % gamesPerRow;
    } else if (numberOfGamesPlug > 0) {
      numberOfGamesPlug = gamesPerRow - numberOfGamesPlug;
    }
  }

  const gamesPlug = createItems({}, numberOfGamesPlug);

  return gamesPlug as GameV1[];
};

const GAME_ITEM_LABELS = ['new', 'top', 'trending'];
const COLLECTION_POSITION = 1;

export const mapGameV2ToGameV1 = (games: GameV2.Game[]): GameV1[] => {
  const mappedGames = games.map((game) => {
    const collections = game.categories.reduce((acc, item) => {
      const label = GAME_ITEM_LABELS.find(
        (gameItemLabel) => gameItemLabel === item
      );
      if (label) return { ...acc, [label]: COLLECTION_POSITION };
      return acc;
    }, {});

    return {
      ...game,
      collections,
      real: game.currencies,
      id: game.identifier,
      fullPath: `${game.provider}/${game.seoTitle}`,
      seoPath: game.seoTitle,
      listCollections: game.categories,
    };
  });
  return mappedGames;
};

export function createGamesListName(param: string, subName?: string) {
  return subName ? `${param}-${subName}` : param;
}
